/**
 * Measurement overlays in 3D (PredictionObject.Rendering.cs / .Connections.cs / TubeRenderer):
 * points as spheres (0.75 mm), the VBR ring as a Catmull-Rom tube (radius 0.15 mm), SOV/STJ
 * contours and free margins as 0.3 mm lines, geometric heights as tubes, height and length
 * lines, diameters, commissural angle wedges, and CSS2D labels. Overlays draw on top of the
 * volume and surfaces (ZTest Always, queue 5000). Each inventory row key maps to one entry.
 */
import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { DEFAULT_COLORS, FREE_MARGIN_COLORS, PREDICTION_COLORS, type Rgba } from '@/inventory/colors';
import { LAYER_OVERLAY } from '@/theater/Viewer';
import {
  closedCatmullRom,
  findPoint,
  formatAngle,
  formatLengthMm,
  measureRing,
  polylineLength,
  type PointEntry,
  type Prediction,
  type RingMetrics,
} from './measurements';

export const MM_TO_UNIT = 0.01;
const POINT_DIAMETER_MM = 0.75;
const LINE_WIDTH_MM = 0.3;
const TUBE_RADIUS_MM = 0.15;
const DIAMETER_LINE_WIDTH_MM = 0.2;
const DIAMETER_SPHERE_MM = 0.3;
const LABEL_UP_MM = 1.5;

export interface PredictionEntry {
  key: string;
  objects: THREE.Object3D[];
  materials: (THREE.MeshBasicMaterial | LineMaterial)[];
  labels: CSS2DObject[];
  color: Rgba;
  /** Inline inventory value, e.g. "1.6 cm", "41.2°", "-" for points. */
  value: string;
  info: [string, string][];
  visible: boolean;
}

export interface CenterlineInput {
  points: THREE.Vector3[]; // scene mm, inferior -> superior
}

const sphereGeometry = new THREE.SphereGeometry(0.5, 20, 14);

let sharedHalo: THREE.Texture | null = null;
/** A white ring on a transparent square: the edit-target halo of a landmark handle. */
function haloTexture(): THREE.Texture {
  if (sharedHalo) return sharedHalo;
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d')!;
  ctx.strokeStyle = 'white';
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.arc(32, 32, 26, 0, Math.PI * 2);
  ctx.stroke();
  sharedHalo = new THREE.CanvasTexture(c);
  return sharedHalo;
}

function overlayMaterial(rgba: Rgba): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color: new THREE.Color(rgba[0], rgba[1], rgba[2]),
    opacity: rgba[3],
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
}

function lineMaterial(rgba: Rgba, widthMm: number): LineMaterial {
  return new LineMaterial({
    color: new THREE.Color(rgba[0], rgba[1], rgba[2]).getHex(),
    opacity: rgba[3],
    transparent: true,
    linewidth: widthMm * MM_TO_UNIT,
    worldUnits: true,
    depthTest: false,
    depthWrite: false,
    // Line2 builds its quads in view space, so the scene root's handedness flip (negative
    // determinant) would make the renderer cull the segment bodies as back faces.
    side: THREE.DoubleSide,
  });
}

function makeLine(points: THREE.Vector3[], rgba: Rgba, widthMm: number, closed = false): { line: Line2; material: LineMaterial } {
  const pts = closed && points.length > 1 ? [...points, points[0]] : points;
  const geometry = new LineGeometry();
  geometry.setPositions(pts.flatMap((p) => [p.x, p.y, p.z]));
  const material = lineMaterial(rgba, widthMm);
  const line = new Line2(geometry, material);
  line.computeLineDistances();
  line.renderOrder = 20;
  line.layers.set(LAYER_OVERLAY);
  return { line, material };
}

/** Tube along a polyline (TubeRenderer with straight segments), radius in mm. */
function makeTube(points: THREE.Vector3[], rgba: Rgba, radiusMm: number, closed = false): { mesh: THREE.Mesh; material: THREE.MeshBasicMaterial } {
  const pts = closed ? [...points, points[0]] : points;
  const path = new THREE.CurvePath<THREE.Vector3>();
  for (let i = 0; i + 1 < pts.length; i++) path.add(new THREE.LineCurve3(pts[i], pts[i + 1]));
  const geometry = new THREE.TubeGeometry(path, Math.max(8, pts.length * 2), radiusMm, 10, false);
  const material = overlayMaterial([rgba[0], rgba[1], rgba[2], 1]); // tubes are opaque on desktop
  const mesh = new THREE.Mesh(geometry, material);
  mesh.renderOrder = 20;
  mesh.layers.set(LAYER_OVERLAY);
  return { mesh, material };
}

function makeSphere(position: THREE.Vector3, rgba: Rgba, diameterMm: number): { mesh: THREE.Mesh; material: THREE.MeshBasicMaterial } {
  const material = overlayMaterial(rgba);
  const mesh = new THREE.Mesh(sphereGeometry, material);
  mesh.position.copy(position);
  mesh.scale.setScalar(diameterMm);
  mesh.renderOrder = 21;
  mesh.layers.set(LAYER_OVERLAY);
  return { mesh, material };
}

function makeLabel(text: string, position: THREE.Vector3): CSS2DObject {
  const el = document.createElement('div');
  el.className = 'label3d';
  el.textContent = text;
  const obj = new CSS2DObject(el);
  obj.position.copy(position);
  obj.layers.set(LAYER_OVERLAY);
  return obj;
}

function slerpUnit(a: THREE.Vector3, b: THREE.Vector3, t: number): THREE.Vector3 {
  const qa = new THREE.Quaternion();
  const qb = new THREE.Quaternion().setFromUnitVectors(a, b);
  qa.slerp(qb, t);
  return a.clone().applyQuaternion(qa).normalize();
}

export class PredictionObject {
  readonly group = new THREE.Group();
  readonly entries = new Map<string, PredictionEntry>();
  readonly prediction: Prediction;
  readonly ringMetrics = new Map<string, RingMetrics>();
  vbrPlane: { center: THREE.Vector3; normal: THREE.Vector3 } | null = null;
  coaptation: THREE.Vector3 | null = null;
  commissures: Partial<Record<'RN' | 'RL' | 'LN', THREE.Vector3>> = {};
  nadirs: Partial<Record<'N' | 'R' | 'L', THREE.Vector3>> = {};
  wedgeApex: THREE.Vector3 | null = null;
  /** The landmark spheres by row key (the drag handles of the correction system). */
  readonly handles = new Map<string, THREE.Mesh>();
  private handleBase = new Map<string, { color: THREE.Color; diameter: number }>();
  private lineMaterials: LineMaterial[] = [];
  private labelsVisible = true;

  constructor(prediction: Prediction, centerline: CenterlineInput | null) {
    this.prediction = prediction;
    this.group.name = 'prediction';
    this.build(centerline);
  }

  // ------------------------------------------------------------ build
  private add(entry: PredictionEntry): void {
    for (const o of entry.objects) this.group.add(o);
    for (const l of entry.labels) this.group.add(l);
    this.entries.set(entry.key, entry);
  }

  private entry(key: string, color: Rgba, value = '-', info: [string, string][] = []): PredictionEntry {
    return { key, objects: [], materials: [], labels: [], color, value, info, visible: true };
  }

  private build(centerline: CenterlineInput | null): void {
    const p = this.prediction;
    const vbr = p.planes.get('VBR');
    if (vbr?.center && vbr.normal) this.vbrPlane = { center: vbr.center.clone(), normal: vbr.normal.clone().normalize() };
    else {
      const ring = p.rings.get('VBR');
      const m = ring ? measureRing(ring) : null;
      if (m) this.vbrPlane = { center: m.centroid, normal: m.normal };
    }
    for (const [name, pts] of p.rings) {
      const m = measureRing(pts);
      if (m) this.ringMetrics.set(name, m);
    }
    this.buildPoints();
    this.buildRings();
    this.buildCurves();
    this.buildConnections();
    this.buildDiameters();
    this.buildWedges(centerline);
  }

  private pointInfo(pt: PointEntry, commissure = false): [string, string][] {
    const raw = { x: pt.position.x, y: pt.position.z, z: pt.position.y }; // back to file axes
    let h = pt.heightToVbr;
    if (commissure && (h === null || Number.isNaN(h))) h = pt.commissuralHeight;
    return [
      ['X', formatLengthMm(raw.x)],
      ['Y', formatLengthMm(raw.y)],
      ['Z', formatLengthMm(raw.z)],
      ['Height from VBR Plane', h === null ? '-' : formatLengthMm(Math.abs(h))],
    ];
  }

  private buildPoints(): void {
    const p = this.prediction;
    const define = (key: string, structure: string, name: string, color: Rgba, commissure = false) => {
      const pt = findPoint(p, structure, name);
      if (!pt) return null;
      const e = this.entry(key, color, '-', this.pointInfo(pt, commissure));
      const s = makeSphere(pt.position, color, POINT_DIAMETER_MM);
      s.mesh.name = `handle:${key}`;
      e.objects.push(s.mesh);
      e.materials.push(s.material);
      this.handles.set(key, s.mesh);
      this.handleBase.set(key, { color: s.material.color.clone(), diameter: POINT_DIAMETER_MM });
      this.add(e);
      return pt.position;
    };
    this.nadirs.L = define('LNadir', 'Nadir', 'L', DEFAULT_COLORS.LNadir) ?? undefined;
    this.nadirs.N = define('NNadir', 'Nadir', 'N', DEFAULT_COLORS.NNadir) ?? undefined;
    this.nadirs.R = define('RNadir', 'Nadir', 'R', DEFAULT_COLORS.RNadir) ?? undefined;
    this.commissures.RL = define('RlCommissure', 'Commissure', 'RL', DEFAULT_COLORS.RlCommissure, true) ?? undefined;
    this.commissures.RN = define('RnCommissure', 'Commissure', 'RN', DEFAULT_COLORS.RnCommissure, true) ?? undefined;
    this.commissures.LN = define('LnCommissure', 'Commissure', 'LN', DEFAULT_COLORS.LnCommissure, true) ?? undefined;
    define('LcaCoronaryOstia', 'Ostium', 'Left Coronary Ostium', DEFAULT_COLORS.LcaCoronaryOstia);
    define('RcaCoronaryOstia', 'Ostium', 'Right Coronary Ostium', DEFAULT_COLORS.RcaCoronaryOstia);
    this.coaptation = define('CentralCoaptationPoint', 'Coaptation', 'Coaptation', DEFAULT_COLORS.CentralCoaptationPoint) ?? null;
    // VBR Plane row: the (hidden) VBR centre point.
    if (this.vbrPlane) {
      const e = this.entry('VbrPlane', DEFAULT_COLORS.VbrPlane, '-', [
        ['Normal Vector', `[${this.vbrPlane.normal.x.toFixed(3)}, ${this.vbrPlane.normal.z.toFixed(3)}, ${this.vbrPlane.normal.y.toFixed(3)}]`],
      ]);
      const s = makeSphere(this.vbrPlane.center, DEFAULT_COLORS.VbrPlane, POINT_DIAMETER_MM);
      e.objects.push(s.mesh);
      e.materials.push(s.material);
      this.add(e);
    }
  }

  private metricValue(structure: string, name: string): number | null {
    const m = this.prediction.metrics.find((x) => x.structure === structure && x.name === name);
    return m && typeof m.value === 'number' ? m.value : null;
  }

  private buildRings(): void {
    const p = this.prediction;
    const ringRow = (key: string, areaKey: string | null, structure: string, color: Rgba, catmull: boolean) => {
      const pts = p.rings.get(structure);
      if (!pts || pts.length < 3) return;
      const m = this.ringMetrics.get(structure);
      const perimeter = this.metricValue(structure, 'perimeter_3D') ?? m?.perimeterMm ?? null;
      const area = this.metricValue(structure, 'area_projected') ?? m?.areaMm2 ?? null;
      const maxD = p.diameters.get(`${structure}|max`)?.value ?? m?.maxChord.length ?? null;
      const minD = p.diameters.get(`${structure}|min`)?.value ?? m?.minChord.length ?? null;
      const info: [string, string][] = [
        ['Perimeter', perimeter === null ? '-' : formatLengthMm(perimeter)],
        ['Area', area === null ? '-' : `${(area / 100).toFixed(1)} cm²`],
        ['Control Points', String(pts.length)],
        ['Ellipticity', maxD && minD ? (Math.abs(minD) / Math.abs(maxD)).toFixed(3) : '-'],
      ];
      const e = this.entry(key, color, perimeter === null ? '-' : formatLengthMm(perimeter), info);
      if (catmull) {
        const smooth = closedCatmullRom(pts, 20, 0.5);
        const t = makeTube(smooth, color, TUBE_RADIUS_MM, true);
        e.objects.push(t.mesh);
        e.materials.push(t.material);
      } else {
        const l = makeLine(pts, color, LINE_WIDTH_MM, true);
        e.objects.push(l.line);
        e.materials.push(l.material);
        this.lineMaterials.push(l.material);
      }
      this.add(e);
      if (areaKey) {
        const a = this.entry(areaKey, color, area === null ? '-' : `${(area / 100).toFixed(1)} cm²`, [['Area', area === null ? '-' : `${(area / 100).toFixed(1)} cm²`]]);
        this.add(a);
      }
    };
    ringRow('VbrEllipse', 'VbrArea', 'VBR', DEFAULT_COLORS.VbrEllipse, true);
    ringRow('SovContour', 'SovArea', 'SOV', DEFAULT_COLORS.SovContour, false);
    ringRow('StjContour', 'StjArea', 'STJ', DEFAULT_COLORS.StjContour, false);
    ringRow('AscAortaContour', null, 'ASCENDINGAORTA', DEFAULT_COLORS.AscAortaContour, false);
  }

  private freeMarginColor(structure: string): Rgba {
    const p = this.prediction;
    const base = structure.startsWith('RN_LN') ? FREE_MARGIN_COLORS.NSinusMargin : structure.startsWith('RN_RL') ? FREE_MARGIN_COLORS.RSinusMargin : structure.startsWith('RL_LN') ? FREE_MARGIN_COLORS.LSinusMargin : FREE_MARGIN_COLORS.Fallback;
    if (!p.fusedMargins.has(structure)) return base;
    const dominant = p.fusedMargins.get(structure) ?? '';
    if (dominant.startsWith('RN_LN')) return FREE_MARGIN_COLORS.FusedRlMargin;
    if (dominant.startsWith('RN_RL')) return FREE_MARGIN_COLORS.FusedNlMargin;
    if (dominant.startsWith('RL_LN')) return FREE_MARGIN_COLORS.FusedNrMargin;
    return base;
  }

  private buildCurves(): void {
    const p = this.prediction;
    const geo = (key: string, structure: string, ghName: string, color: Rgba) => {
      const pts = p.curves.get(structure);
      if (!pts || pts.length < 2) return;
      const length = this.metricValue(structure, 'length') ?? polylineLength(pts);
      const info: [string, string][] = [
        ['Control Points', String(pts.length)],
        ['Length', formatLengthMm(length)],
      ];
      const e = this.entry(key, color, formatLengthMm(length), info);
      const t = makeTube(pts, color, TUBE_RADIUS_MM, false);
      e.objects.push(t.mesh);
      e.materials.push(t.material);
      const mid = pts[Math.floor(pts.length / 2)].clone().add(new THREE.Vector3(0, LABEL_UP_MM, 0));
      e.labels.push(makeLabel(`${ghName}\n${formatLengthMm(length)}`, mid));
      this.add(e);
    };
    geo('NGeometricHeight', 'NGeometricHeight', 'N Geometric', DEFAULT_COLORS.NGeometricHeight);
    geo('RGeometricHeight', 'RGeometricHeight', 'R Geometric', DEFAULT_COLORS.RGeometricHeight);
    geo('LGeometricHeight', 'LGeometricHeight', 'L Geometric', DEFAULT_COLORS.LGeometricHeight);
    const margin = (key: string, structure: string) => {
      const pts = p.curves.get(structure);
      if (!pts || pts.length < 2) return;
      const color = this.freeMarginColor(structure);
      const length = this.metricValue(structure, 'length') ?? polylineLength(pts);
      const info: [string, string][] = [
        ['Control Points', String(pts.length)],
        ['Length', formatLengthMm(length)],
      ];
      if (p.fusedMargins.has(structure)) {
        const notes = p.metrics.find((m) => m.structure === structure && m.name === 'fused')?.notes;
        info.push(['Fused Edge', notes ? `Yes (${notes})` : 'Yes']);
      }
      const e = this.entry(key, color, formatLengthMm(length), info);
      const l = makeLine(pts, color, LINE_WIDTH_MM);
      e.objects.push(l.line);
      e.materials.push(l.material);
      this.lineMaterials.push(l.material);
      this.add(e);
    };
    margin('RnLnFreeMargin', 'RN_LN_FreeMargin');
    margin('RnRlFreeMargin', 'RN_RL_FreeMargin');
    margin('RlLnFreeMargin', 'RL_LN_FreeMargin');
  }

  private footOnVbr(pt: THREE.Vector3): THREE.Vector3 | null {
    if (!this.vbrPlane) return null;
    const d = pt.clone().sub(this.vbrPlane.center).dot(this.vbrPlane.normal);
    return pt.clone().sub(this.vbrPlane.normal.clone().multiplyScalar(d));
  }

  private connection(key: string, labelName: string, a: THREE.Vector3 | null | undefined, b: THREE.Vector3 | null | undefined, color: Rgba, widthMm = LINE_WIDTH_MM, withLabel = true): void {
    if (!a || !b) return;
    const length = a.distanceTo(b);
    const e = this.entry(key, color, formatLengthMm(length), [['Value', formatLengthMm(length)]]);
    const l = makeLine([a, b], color, widthMm);
    e.objects.push(l.line);
    e.materials.push(l.material);
    this.lineMaterials.push(l.material);
    if (withLabel) e.labels.push(makeLabel(`${labelName}\n${formatLengthMm(length)}`, a.clone().add(b).multiplyScalar(0.5)));
    this.add(e);
  }

  private buildConnections(): void {
    const p = this.prediction;
    const vbrCenter = this.vbrPlane?.center ?? null;
    const stjCenter = this.ringMetrics.get('STJ')?.centroid ?? findPoint(p, 'STJ', 'center')?.position ?? null;
    const sovCenter = this.ringMetrics.get('SOV')?.centroid ?? findPoint(p, 'SOV', 'center')?.position ?? null;
    const ascCenter = this.ringMetrics.get('ASCENDINGAORTA')?.centroid ?? null;
    this.connection('StjHeight', 'STJ Height', vbrCenter, stjCenter, DEFAULT_COLORS.StjHeight);
    this.connection('SovHeight', 'SOV Height', vbrCenter, sovCenter, DEFAULT_COLORS.SovHeight);
    this.connection('AscAortaHeight', 'Ascending Aorta Height', stjCenter, ascCenter, DEFAULT_COLORS.AscAortaHeight);
    const along = (key: string, label: string, pt: THREE.Vector3 | undefined | null, color: Rgba) => {
      if (!pt) return;
      this.connection(key, label, pt, this.footOnVbr(pt), color);
    };
    along('RnHeight', 'RN Height', this.commissures.RN, DEFAULT_COLORS.RnHeight);
    along('RlHeight', 'RL Height', this.commissures.RL, DEFAULT_COLORS.RlHeight);
    along('LnHeight', 'LN Height', this.commissures.LN, DEFAULT_COLORS.LnHeight);
    along('RcaHeight', 'RCA Height', findPoint(p, 'Ostium', 'Right Coronary Ostium')?.position, DEFAULT_COLORS.RcaHeight);
    along('LcaHeight', 'LCA Height', findPoint(p, 'Ostium', 'Left Coronary Ostium')?.position, DEFAULT_COLORS.LcaHeight);
    along('EffectiveHeight', 'Effective Height', this.coaptation, DEFAULT_COLORS.EffectiveHeight);
    // Intercommissural lengths (no labels: skip rules)
    this.connection('RlRnLength', 'RL-RN', this.commissures.RL, this.commissures.RN, DEFAULT_COLORS.RlRnLength, LINE_WIDTH_MM, false);
    this.connection('RlLnLength', 'LN-RL', this.commissures.RL, this.commissures.LN, DEFAULT_COLORS.RlLnLength, LINE_WIDTH_MM, false);
    this.connection('RnLnLength', 'LN-RN', this.commissures.LN, this.commissures.RN, DEFAULT_COLORS.RnLnLength, LINE_WIDTH_MM, false);
  }

  private buildDiameters(): void {
    const p = this.prediction;
    const diam = (key: string, structure: string, name: 'max' | 'min', label: string, color: Rgba) => {
      const pts = p.rings.get(structure);
      const m = this.ringMetrics.get(structure);
      if (!pts || !m) return;
      const chord = name === 'max' ? m.maxChord : m.minChord;
      const p1 = pts[chord.a];
      const p2 = pts[chord.b];
      const value = p.diameters.get(`${structure}|${name}`)?.value ?? chord.length;
      const info: [string, string][] = [
        ['Value', formatLengthMm(value)],
        ['Point 1', `[${p1.x.toFixed(2)}, ${p1.z.toFixed(2)}, ${p1.y.toFixed(2)}]`],
        ['Point 2', `[${p2.x.toFixed(2)}, ${p2.z.toFixed(2)}, ${p2.y.toFixed(2)}]`],
      ];
      const e = this.entry(key, color, formatLengthMm(value), info);
      const l = makeLine([p1, p2], color, DIAMETER_LINE_WIDTH_MM);
      e.objects.push(l.line);
      e.materials.push(l.material);
      this.lineMaterials.push(l.material);
      for (const q of [p1, p2]) {
        const s = makeSphere(q, PREDICTION_COLORS.diameterDefault, DIAMETER_SPHERE_MM);
        e.objects.push(s.mesh);
        e.materials.push(s.material);
      }
      e.labels.push(makeLabel(`${label} ${name}\n${formatLengthMm(value)}`, p1.clone()));
      this.add(e);
    };
    diam('VbrMaxDiameter', 'VBR', 'max', 'VBR', DEFAULT_COLORS.VbrMaxDiameter);
    diam('VbrMinDiameter', 'VBR', 'min', 'VBR', DEFAULT_COLORS.VbrMinDiameter);
    diam('SovMaxDiameter', 'SOV', 'max', 'SOV', DEFAULT_COLORS.SovMaxDiameter);
    diam('SovMinDiameter', 'SOV', 'min', 'SOV', DEFAULT_COLORS.SovMinDiameter);
    diam('StjMaxDiameter', 'STJ', 'max', 'STJ', DEFAULT_COLORS.StjMaxDiameter);
    diam('StjMinDiameter', 'STJ', 'min', 'STJ', DEFAULT_COLORS.StjMinDiameter);
    diam('AscAortaMaxDiameter', 'ASCENDINGAORTA', 'max', 'Ascending Aorta', DEFAULT_COLORS.AscAortaMaxDiameter);
    diam('AscAortaMinDiameter', 'ASCENDINGAORTA', 'min', 'Ascending Aorta', DEFAULT_COLORS.AscAortaMinDiameter);
  }

  /** Commissure plane: normal = cross(rl - rn, ln - rn), point = centroid. Apex = where that
   * plane crosses the centerline (else the closest centerline point, else the centroid). */
  private buildWedges(centerline: CenterlineInput | null): void {
    const rn = this.commissures.RN;
    const rl = this.commissures.RL;
    const ln = this.commissures.LN;
    if (!rn || !rl || !ln) return;
    const planeNormal = new THREE.Vector3().crossVectors(rl.clone().sub(rn), ln.clone().sub(rn)).normalize();
    const centroid = rn.clone().add(rl).add(ln).multiplyScalar(1 / 3);
    let apex = centroid.clone();
    let tangent: THREE.Vector3 | null = null;
    if (centerline && centerline.points.length >= 2) {
      const pts = centerline.points;
      let found: THREE.Vector3 | null = null;
      for (let i = 0; i + 1 < pts.length; i++) {
        const da = pts[i].clone().sub(centroid).dot(planeNormal);
        const db = pts[i + 1].clone().sub(centroid).dot(planeNormal);
        if ((da <= 0 && db >= 0) || (da >= 0 && db <= 0)) {
          const t = Math.abs(da - db) < 1e-9 ? 0 : da / (da - db);
          found = pts[i].clone().lerp(pts[i + 1], t);
          tangent = pts[i + 1].clone().sub(pts[i]).normalize();
          break;
        }
      }
      if (!found) {
        let best = Infinity;
        let bi = 0;
        pts.forEach((q, i) => {
          const d = Math.abs(q.clone().sub(centroid).dot(planeNormal));
          if (d < best) {
            best = d;
            bi = i;
          }
        });
        found = pts[bi].clone();
        const n = pts[Math.min(bi + 1, pts.length - 1)].clone().sub(pts[Math.max(bi - 1, 0)]);
        tangent = n.lengthSq() > 0 ? n.normalize() : null;
      }
      apex = found;
    }
    this.wedgeApex = apex;
    // Apex control point (purple)
    const apexEntry = this.entry('CommissuralAnglePoint', DEFAULT_COLORS.CommissuralAnglePoint, '-');
    const s = makeSphere(apex, DEFAULT_COLORS.CommissuralAnglePoint, POINT_DIAMETER_MM);
    apexEntry.objects.push(s.mesh);
    apexEntry.materials.push(s.material);
    this.add(apexEntry);

    // C7 wedge normal: slerp(vbrNormal, valveAxis, clamp01(apexHeight / stjHeight))
    let wedgeNormal = planeNormal.clone();
    if (this.vbrPlane) {
      const vbrN = this.vbrPlane.normal.clone();
      const stjCenter = this.ringMetrics.get('STJ')?.centroid ?? null;
      if (vbrN.dot(stjCenter ? stjCenter.clone().sub(this.vbrPlane.center) : apex.clone().sub(this.vbrPlane.center)) < 0) vbrN.negate();
      let valveAxis = tangent ? tangent.clone() : stjCenter ? stjCenter.clone().sub(this.vbrPlane.center).normalize() : vbrN.clone();
      if (valveAxis.dot(vbrN) < 0) valveAxis = valveAxis.negate();
      const apexHeight = apex.clone().sub(this.vbrPlane.center).dot(vbrN);
      const stjHeight = stjCenter ? stjCenter.clone().sub(this.vbrPlane.center).dot(vbrN) : 0;
      const frac = stjHeight > 1e-6 ? Math.min(1, Math.max(0, apexHeight / stjHeight)) : 0.5;
      wedgeNormal = slerpUnit(vbrN, valveAxis, frac);
    }
    const project = (q: THREE.Vector3) => q.clone().sub(wedgeNormal.clone().multiplyScalar(q.clone().sub(apex).dot(wedgeNormal)));
    const pRn = project(rn);
    const pRl = project(rl);
    const pLn = project(ln);
    const lengths = [pRn, pRl, pLn].map((q) => q.distanceTo(apex));
    const radius = lengths.every((l) => l <= 1e-2) ? 1 : Math.min(5, Math.max(0.1, Math.min(...lengths) * 0.25));
    const wedge = (key: string, from: THREE.Vector3, to: THREE.Vector3, third: THREE.Vector3, color: Rgba) => {
      const u = from.clone().sub(apex).normalize();
      const v = new THREE.Vector3().crossVectors(wedgeNormal, u).normalize();
      const dir2 = to.clone().sub(apex).normalize();
      let angle = Math.atan2(dir2.dot(v), dir2.dot(u));
      if (Math.abs(angle) < THREE.MathUtils.degToRad(0.1)) return;
      const dir3 = third.clone().sub(apex).normalize();
      const a3 = Math.atan2(dir3.dot(v), dir3.dot(u));
      const inside = angle > 0 ? a3 > 0.0009 && a3 < angle - 0.0009 : a3 < -0.0009 && a3 > angle + 0.0009;
      if (inside) angle = angle > 0 ? angle - Math.PI * 2 : angle + Math.PI * 2;
      const pts: THREE.Vector3[] = [];
      for (let i = 0; i <= 48; i++) {
        const a = (angle * i) / 48;
        pts.push(apex.clone().add(u.clone().multiplyScalar(Math.cos(a) * radius)).add(v.clone().multiplyScalar(Math.sin(a) * radius)));
      }
      const deg = Math.abs(THREE.MathUtils.radToDeg(angle));
      const e = this.entry(key, color, formatAngle(deg), [['Value', formatAngle(deg)]]);
      const t = makeTube(pts, color, TUBE_RADIUS_MM, false);
      e.objects.push(t.mesh);
      e.materials.push(t.material);
      const midA = angle / 2;
      const midDir = u.clone().multiplyScalar(Math.cos(midA)).add(v.clone().multiplyScalar(Math.sin(midA)));
      e.labels.push(makeLabel(formatAngle(deg), apex.clone().add(midDir.multiplyScalar(radius * 1.05)).add(wedgeNormal.clone().multiplyScalar(radius * 0.05))));
      this.add(e);
    };
    wedge('RAngle', rn, rl, ln, DEFAULT_COLORS.RAngle);
    wedge('LAngle', ln, rl, rn, DEFAULT_COLORS.LAngle);
    wedge('NAngle', ln, rn, rl, DEFAULT_COLORS.NAngle);
  }

  // ------------------------------------------------------------ API
  /** Edit-target / hover / drag look of a landmark handle (HighlightPlus profiles): the sphere
   * grows and brightens; `editing` adds a halo ring so the adjustable points stand out. */
  setHandleState(key: string, state: { editing: boolean; hover: boolean; dragging: boolean }): void {
    const mesh = this.handles.get(key);
    const base = this.handleBase.get(key);
    if (!mesh || !base) return;
    const scale = state.dragging ? 1.6 : state.hover ? 1.35 : state.editing ? 1.2 : 1;
    mesh.scale.setScalar(base.diameter * scale);
    const m = mesh.material as THREE.MeshBasicMaterial;
    const tint = state.dragging || state.hover ? 0.55 : state.editing ? 0.3 : 0;
    m.color.copy(base.color).lerp(new THREE.Color(1, 1, 1), tint);
    let halo = mesh.getObjectByName('halo') as THREE.Sprite | undefined;
    if (state.editing && !halo) {
      halo = new THREE.Sprite(new THREE.SpriteMaterial({ map: haloTexture(), transparent: true, opacity: 0.9, depthTest: false, depthWrite: false }));
      halo.name = 'halo';
      halo.scale.setScalar(2.6); // in sphere units: the sphere is a unit sphere scaled to its diameter
      halo.renderOrder = 22;
      halo.layers.set(LAYER_OVERLAY);
      mesh.add(halo);
    } else if (!state.editing && halo) {
      mesh.remove(halo);
      halo.material.dispose();
    }
  }

  setVisible(key: string, visible: boolean): void {
    const e = this.entries.get(key);
    if (!e) return;
    e.visible = visible;
    for (const o of e.objects) o.visible = visible;
    for (const l of e.labels) l.visible = visible && this.labelsVisible;
  }

  setLabelsVisible(on: boolean): void {
    this.labelsVisible = on;
    for (const e of this.entries.values()) for (const l of e.labels) l.visible = on && e.visible;
  }

  setColor(key: string, rgba: Rgba): void {
    const e = this.entries.get(key);
    if (!e) return;
    e.color = rgba;
    for (const m of e.materials) {
      if ((m as LineMaterial).isLineMaterial) {
        (m as LineMaterial).color.setRGB(rgba[0], rgba[1], rgba[2]);
        m.opacity = rgba[3];
      } else {
        (m as THREE.MeshBasicMaterial).color.setRGB(rgba[0], rgba[1], rgba[2]);
        // tubes stay opaque (UpdateGameObjectColor forces alpha 1 on MeshRenderers)
        m.opacity = (m as THREE.MeshBasicMaterial).userData.tube ? 1 : rgba[3];
      }
    }
  }

  setResolution(width: number, height: number): void {
    for (const m of this.lineMaterials) m.resolution.set(width, height);
  }

  /** World AABB of everything drawn (for framing). */
  bounds(): THREE.Box3 {
    const box = new THREE.Box3();
    for (const e of this.entries.values()) for (const o of e.objects) box.expandByObject(o);
    return box;
  }

  /** FramingRadius inputs: ring vertices, coaptation, commissures, nadirs (scene mm). */
  framingPoints(): THREE.Vector3[] {
    const pts: THREE.Vector3[] = [];
    for (const key of ['VBR', 'SOV', 'STJ']) for (const q of this.prediction.rings.get(key) ?? []) pts.push(q);
    if (this.coaptation) pts.push(this.coaptation);
    for (const q of Object.values(this.commissures)) if (q) pts.push(q);
    for (const q of Object.values(this.nadirs)) if (q) pts.push(q);
    return pts;
  }

  dispose(): void {
    for (const e of this.entries.values()) {
      for (const o of e.objects) {
        const m = o as THREE.Mesh;
        if (m.geometry && m.geometry !== sphereGeometry) m.geometry.dispose();
      }
      for (const m of e.materials) m.dispose();
      for (const l of e.labels) l.element.remove();
    }
  }
}
